import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { CardPackCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { GlareEffect } from '@sorare/core/src/atoms/animations/GlareEffect';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, BrandM } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_SERIES_MARKET_COMMON_PACKS_GROUP } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { CARD_SIZE } from '@sorare/core/src/lib/cards';
import type { Unit } from '@sorare/core/src/lib/style';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { BuyPackButton } from '#src/components/commonCard/BuyPackButton/index.tsx';
import { EligiblePlayers } from '#src/components/commonCard/BuyPacks/EligiblePlayers/index.tsx';

import { Ellipse } from './Ellipse/index.tsx';
import type { PackWithBuyButton_cardPack } from './__generated__/index.graphql.ts';

const PACK_IMAGE_WIDTH = 120;

const HorizontalRoot = styled(Horizontal)`
  position: relative;
  width: 100%;
  align-items: flex-start;
  padding: var(--intermediate-unit) ${PACK_IMAGE_WIDTH}px
    var(--intermediate-unit) var(--intermediate-unit);
  background: var(--c-nd-50);
  backdrop-filter: blur(var(--unit));
  border: 1px solid var(--c-nd-50);
  border-radius: var(--unit);
  overflow: hidden;
  isolation: isolate;
`;

const HorizontalContent = styled(Vertical).attrs({ gap: 1.5 })`
  flex: 1;
  align-items: flex-start;
`;

const HorizontalPackImage = styled.img`
  position: absolute;
  right: var(--intermediate-unit);
  top: var(--intermediate-unit);
  width: ${PACK_IMAGE_WIDTH}px;
  object-fit: contain;
  opacity: 0.4;
  pointer-events: none;
`;

const Root = styled(Vertical)`
  position: relative;
  isolation: isolate;
  will-change: transform;
  justify-content: space-between;
  transition: transform 0.3s ease-in-out;
  &.scaleOnHover:hover,
  &.scaleOnHover:focus {
    transform: scale(1.02);
  }
`;

const Postionned = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  > svg {
    width: 100%;
    height: 100%;
  }
`;

const PackImageWrapper = styled(Horizontal).attrs({ center: true })`
  position: relative;
  isolation: isolate;
`;

const PositionnedEligiblePlayers = styled.div`
  position: absolute;
  bottom: calc(-1 * var(--unit));
  right: calc(-1 * var(--quadruple-unit));
  left: calc(-1 * var(--quadruple-unit));
`;

const PackImg = styled(GlareEffect)`
  position: relative;
  object-fit: contain;
  height: 100%;
  width: calc(12 * var(--unit));
  @media ${mobileAndAbove} {
    width: calc(16 * var(--unit));
  }
`;

type Props = {
  cardPack: PackWithBuyButton_cardPack;
  cardPackGroupSlug: string;
  gap?: Unit;
  isPremiumPack?: boolean;
  scaleOnHover?: boolean;
  withEligiblePlayers?: boolean;
  variant?: 'horizontal' | 'vertical';
};
export const PackWithBuyButton = ({
  cardPack,
  cardPackGroupSlug,
  gap = 1,
  isPremiumPack = false,
  scaleOnHover = true,
  withEligiblePlayers = false,
  variant = 'vertical',
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const {
    flags: { useColorsMarket = false },
  } = useFeatureFlags();

  const isGemsPack = cardPack?.currency === CardPackCurrency.COMMON_GEM;
  const commonPlayers = cardPack?.eligibleCommonPlayers;
  const isLaptopAndAbove = useIsLaptop();
  const buyButton = (
    <BuyPackButton
      cardPack={cardPack}
      size="small"
      color={isGemsPack ? 'primary' : 'secondary'}
      confirmPurchaseColor={isGemsPack ? 'secondary' : 'primary'}
    />
  );

  if (variant === 'horizontal') {
    return (
      <HorizontalRoot>
        <HorizontalContent>
          <Vertical gap={0.5} style={{ justifyContent: 'flex-start' }}>
            {!!cardPack.title && (
              <BrandM as="h3" color="var(--c-white)">
                {cardPack.title}
              </BrandM>
            )}
            {!!cardPack.description && (
              <BodyS as="p" color="var(--c-nd-600)">
                {cardPack.description}
              </BodyS>
            )}
          </Vertical>
          <Horizontal>
            <BuyPackButton
              cardPack={cardPack}
              size="small"
              color="primary"
              confirmPurchaseColor="secondary"
            />
          </Horizontal>
        </HorizontalContent>
        {!!cardPack.pictureUrl && (
          <HorizontalPackImage src={cardPack.pictureUrl} alt="" />
        )}
      </HorizontalRoot>
    );
  }

  const displayDetails = useColorsMarket && isLaptopAndAbove;
  return (
    <Root
      center
      gap={gap}
      className={classNames({
        scaleOnHover,
        isPremiumPack,
      })}
    >
      {!!isPremiumPack && (
        <Postionned>
          <Ellipse color={cardPack.primaryColor} />
        </Postionned>
      )}
      <PackImageWrapper>
        <Link
          to={`${generateSportContextPath(
            ANY_SPORT_SERIES_MARKET_COMMON_PACKS_GROUP,
            {
              params: {
                group: cardPackGroupSlug,
              },
            }
          )}?pack=${cardPack.slug}`}
        >
          <PackImg
            pictureUrl={proxyUrl(cardPack.pictureUrl, {
              cropWidth: CARD_SIZE,
            })}
          />
          {!!withEligiblePlayers && commonPlayers?.length > 0 && (
            <PositionnedEligiblePlayers>
              <EligiblePlayers
                commonPlayers={commonPlayers}
                color={cardPack?.primaryColor}
              />
            </PositionnedEligiblePlayers>
          )}
        </Link>
      </PackImageWrapper>
      {!!displayDetails && (!!cardPack.title || !!cardPack.description) && (
        <Vertical gap={0.5} center className="text-center">
          {!!cardPack.title && (
            <BrandM as="h3" color="var(--c-white)">
              {cardPack.title}
            </BrandM>
          )}
          {!!cardPack.description && (
            <BodyS as="p" color="var(--c-nd-600)">
              {cardPack.description}
            </BodyS>
          )}
        </Vertical>
      )}
      {buyButton}
    </Root>
  );
};

PackWithBuyButton.fragments = {
  cardPack: gql`
    fragment PackWithBuyButton_cardPack on CardPack {
      slug
      title
      description
      pictureUrl
      primaryColor: color(variant: PRIMARY)
      currency
      eligibleCommonPlayers {
        id
        ...EligiblePlayers_commonPlayer
      }
      ...BuyPackButton_cardPack
    }
    ${EligiblePlayers.fragments.commonPlayer}
    ${BuyPackButton.fragments.cardPack}
  ` as TypedDocumentNode<PackWithBuyButton_cardPack>,
};
